import { plus } from "@/math/coordinates";
import { initViewportCoord } from "@/model/coordinates";
import Resolvable from "@/utils/async/Resolvable";

import { contentSize, isInsideCoords } from "./coordinates";

export interface ElementState {
  elements: Array<{ element: HTMLElement }>;
  activeElement: HTMLElement | null;
}

/**
 * Hides the specified elements by setting their display property to "none".
 * Returns the state of the elements before hiding.
 *
 * @param elements - The list of elements to hide.
 * @returns The state of the elements before hiding.
 */
export function hideElements(elements: NodeListOf<HTMLElement>): ElementState {
  const state: ElementState = {
    elements: [],
    activeElement: document.activeElement as HTMLElement | null,
  };

  elements.forEach((element) => {
    element.style.display = "none";

    if (!element.classList.contains("moving") && !element.classList.contains("deleting")) {
      state.elements.push({ element });
    }
  });

  return state;
}

/**
 * Shows the elements in the given state by setting their display style to an empty string.
 * If there is an active element in the state, it will be focused.
 *
 * @param state - The element state containing the elements to be shown.
 */
export function showElements(state: ElementState) {
  state.elements.forEach((item) => {
    item.element.style.display = "";
  });

  // only focus when the sticky note is inside the viewport
  const stickyNoteEl = state.activeElement?.closest<HTMLElement>(".sticky-note");

  if (!stickyNoteEl) return;
  if (!isElementInViewport(stickyNoteEl)) return;

  state.activeElement?.focus?.();
}

export function isBrowserTabActive() {
  return document.visibilityState !== "hidden";
}

export function untilBrowserTabVisible(): Resolvable<boolean> {
  const resolvable = new Resolvable<boolean>();
  if (isBrowserTabActive()) {
    resolvable.resolve(true);
  } else {
    document.addEventListener("visibilitychange", () => resolvable.resolve(false), {
      once: true,
    });
  }
  return resolvable;
}

export function createTspan(text: string, x: number, y: number) {
  const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
  tspan.appendChild(document.createTextNode(text));
  tspan.setAttribute("x", "" + x);
  tspan.setAttribute("y", "" + y);
  return tspan;
}

export async function fontsLoaded(): Promise<unknown> {
  return document.fonts?.ready ?? true;
}

export function terminateEvent(event: Event) {
  event.stopPropagation();
  event.preventDefault();
}

export function addFontFamily(el: HTMLElement, fontFamily: string) {
  fontFamily = `"${fontFamily}"`;
  const fonts = getComputedStyle(el).fontFamily.split(/,\s*/);
  if (!fonts.includes(fontFamily)) {
    // add it at second position: behind default font, but before all fallbacks
    fonts.splice(1, 0, fontFamily);
    el.style.fontFamily = fonts.join(",");
  }
}

export function isElementInViewport(el: HTMLElement | null): boolean {
  return !!el && isInsideCoords(el, initViewportCoord(), plus(initViewportCoord(), contentSize()));
}

export const isMac = navigator.platform.includes("Mac");

export const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

export const lineSeparator = navigator.userAgent.includes("Windows") ? "\r\n" : "\n";

export function whenChildElementWithClass(
  element: Element,
  className: string,
  action: (child: Element) => void,
) {
  const child = element.querySelector(`.${className}`);
  if (child) {
    action(child);
  } else {
    const observer = new window.MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element && node.classList.contains(className)) {
            observer.disconnect();
            action(node);
          }
        });
      }
    }, true);
    observer.observe(element, { childList: true });
    setTimeout(() => observer.disconnect(), 10000);
  }
}

// target is never null for events dispatched by the browser
export function eventTarget<T extends HTMLElement>(e: Event): T {
  return e.target as T;
}

// currentTarget is only null outside of event dispatch; inside a handler it's always set
export function eventHandlerElem<T extends HTMLElement>(e: Event): T {
  return e.currentTarget as T;
}

export function blurHandlerElem(e: MouseEvent) {
  eventHandlerElem(e).blur();
}

export function isInput(el: HTMLElement) {
  // this is editor specific
  return (
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.nodeName) ||
    isEditorLine(el) ||
    (el.nodeName === "BR" && (el.parentElement === null || isEditorLine(el.parentElement))) // this happens when pasting into an empty code mirror
  );

  function isEditorLine(el: HTMLElement) {
    return el.classList?.contains("cm-line");
  }
}

export function stopScrollPropagation(e: WheelEvent) {
  const target = eventHandlerElem(e);
  if (
    (e.deltaY < 0 && target.scrollTop === 0) ||
    (e.deltaY > 0 && target.scrollTop === target.scrollHeight - target.offsetHeight)
  ) {
    e.preventDefault();
  }
}

export function releaseCapture(e: PointerEvent) {
  document.documentElement.setPointerCapture?.(e.pointerId);
  document.documentElement.releasePointerCapture?.(e.pointerId);
}

export function isLeftClick(e: PointerEvent) {
  return e.button === 0;
}

export function isRightClick(e: PointerEvent) {
  return e.button === 2;
}

export function isMiddleClick(e: PointerEvent) {
  return e.button === 1;
}

export function isLinkDrag(e: Event) {
  return eventTarget(e).classList.contains("link-drag");
}
